import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance, { tenantBaseURL } from '../axios/axiosinstance';

// ---------- Master data ----------
export const useEmployers = () =>
  useQuery({
    queryKey: ['form16-employers'],
    queryFn: () => axiosInstance.get('api/employers/').then((r) => r.data),
  });

export const useUpsertEmployer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      id
        ? axiosInstance.put(`api/employers/${id}/`, payload).then((r) => r.data)
        : axiosInstance.post('api/employers/', payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form16-employers'] }),
  });
};

export const useFinancialYears = () =>
  useQuery({
    queryKey: ['form16-fys'],
    queryFn: () => axiosInstance.get('api/financial-years/').then((r) => r.data),
  });

export const useBranches = () =>
  useQuery({
    queryKey: ['form16-branches'],
    queryFn: () => axiosInstance.get('api/branches/').then((r) => r.data),
  });

export const useCreateFinancialYear = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      axiosInstance.post('api/financial-years/', payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form16-fys'] }),
  });
};

// ---------- Tax declarations (employee) ----------
export const useMyDeclaration = (employeeNo, fyId) =>
  useQuery({
    queryKey: ['my-declaration', employeeNo, fyId],
    queryFn: () =>
      axiosInstance
        .get(`api/declarations/${employeeNo}/${fyId}/`)
        .then((r) => r.data),
    enabled: !!employeeNo && !!fyId,
  });

export const useSaveDeclaration = (employeeNo, fyId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      axiosInstance
        .patch(`api/declarations/${employeeNo}/${fyId}/`, payload)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-declaration', employeeNo, fyId] }),
  });
};

export const useSubmitDeclaration = (employeeNo, fyId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      axiosInstance
        .post(`api/declarations/${employeeNo}/${fyId}/submit/`)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-declaration', employeeNo, fyId] }),
  });
};

export const useUploadProof = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ declarationId, sectionCode, file, description }) => {
      const fd = new FormData();
      fd.append('declaration', declarationId);
      fd.append('section_code', sectionCode);
      fd.append('file', file);
      if (description) fd.append('description', description);
      return axiosInstance
        .post('api/declarations/proofs/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-declaration'] }),
  });
};

export const useDeleteProof = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proofId) =>
      axiosInstance.delete(`api/declarations/proofs/${proofId}/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-declaration'] }),
  });
};

// ---------- Tax declarations (admin) ----------
export const useDeclarationsAdmin = (params = {}) =>
  useQuery({
    queryKey: ['declarations-admin', params],
    queryFn: () =>
      axiosInstance.get('api/declarations/admin/', { params }).then((r) => r.data),
  });

export const useApproveDeclaration = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewerId }) =>
      axiosInstance
        .post(`api/declarations/${id}/approve/`, { reviewer_id: reviewerId })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['declarations-admin'] }),
  });
};

export const useRejectDeclaration = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason, reviewerId }) =>
      axiosInstance
        .post(`api/declarations/${id}/reject/`, { reason, reviewer_id: reviewerId })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['declarations-admin'] }),
  });
};

// ---------- Form 16 admin ----------
export const useForm16AdminList = (params = {}) =>
  useQuery({
    queryKey: ['form16-admin-list', params],
    queryFn: () =>
      axiosInstance.get('api/form16/admin/', { params }).then((r) => r.data),
    keepPreviousData: true,
  });

export const useForm16Detail = (id) =>
  useQuery({
    queryKey: ['form16-detail', id],
    queryFn: () => axiosInstance.get(`api/form16/admin/${id}/`).then((r) => r.data),
    enabled: !!id,
  });

export const useGenerateForm16 = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeNo, fyId, employerId }) =>
      axiosInstance
        .post(`api/form16/${employeeNo}/${fyId}/generate/`, { employer_id: employerId })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form16-admin-list'] });
      qc.invalidateQueries({ queryKey: ['form16-detail'] });
    },
  });
};

export const useImportSalaryEntries = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, month, year }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('month', month);
      fd.append('year', year);
      return axiosInstance
        .post('api/form16/salary-entries/import/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form16-admin-list'] }),
  });
};

export const useBulkGenerateForm16 = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      axiosInstance.post('api/form16/bulk-generate/', payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form16-admin-list'] }),
  });
};

export const useRefreshForm16 = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      axiosInstance.post(`api/form16/${id}/refresh/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form16-admin-list'] });
      qc.invalidateQueries({ queryKey: ['form16-detail'] });
    },
  });
};

// Admin manual edit of Part B leaf fields (sticky overrides + recompute).
export const useUpdateForm16PartB = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      axiosInstance.patch(`api/form16/${id}/part-b/`, payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form16-detail'] });
    },
  });
};

export const usePublishForm16 = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      axiosInstance.post(`api/form16/${id}/publish/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form16-admin-list'] });
      qc.invalidateQueries({ queryKey: ['form16-detail'] });
    },
  });
};

export const useRevokeForm16 = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      axiosInstance.post(`api/form16/${id}/revoke/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form16-admin-list'] });
      qc.invalidateQueries({ queryKey: ['form16-detail'] });
    },
  });
};

// ---------- Form 16 email (preview-then-send, mirrors email_payslips) ----------
// Preview-only call — no send, no writes. Same request shape as the actual
// send, just tagged `preview: true`. Selection-driven: pass either
// { form16_id } (single) or { form16_ids: [...] } (bulk).
export const previewForm16Emails = (payload) =>
  axiosInstance.post('api/form16/email/', { ...payload, preview: true }).then((r) => r.data);

export const useEmailForm16 = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      axiosInstance.post('api/form16/email/', payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form16-admin-list'] }),
  });
};

export const useReconciliation = (id) =>
  useQuery({
    queryKey: ['form16-reconciliation', id],
    queryFn: () =>
      axiosInstance.get(`api/form16/${id}/reconciliation/`).then((r) => r.data),
    enabled: !!id,
  });

// ---------- Annexure A/B ----------
export const useDeposits = (partAId) =>
  useQuery({
    queryKey: ['form16-deposits', partAId],
    queryFn: () =>
      axiosInstance
        .get('api/form16/annexure/deposits/', { params: { part_a_id: partAId } })
        .then((r) => r.data),
    enabled: !!partAId,
  });

export const useUpsertDeposit = (partAId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      id
        ? axiosInstance
            .put(`api/form16/annexure/deposits/${id}/`, payload)
            .then((r) => r.data)
        : axiosInstance.post('api/form16/annexure/deposits/', payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form16-deposits', partAId] }),
  });
};

export const useDeleteDeposit = (partAId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(`api/form16/annexure/deposits/${id}/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form16-deposits', partAId] }),
  });
};

export const useChallans = (partAId) =>
  useQuery({
    queryKey: ['form16-challans', partAId],
    queryFn: () =>
      axiosInstance
        .get('api/form16/annexure/challans/', { params: { part_a_id: partAId } })
        .then((r) => r.data),
    enabled: !!partAId,
  });

export const useUpsertChallan = (partAId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      id
        ? axiosInstance
            .put(`api/form16/annexure/challans/${id}/`, payload)
            .then((r) => r.data)
        : axiosInstance.post('api/form16/annexure/challans/', payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form16-challans', partAId] }),
  });
};

export const useDeleteChallan = (partAId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(`api/form16/annexure/challans/${id}/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form16-challans', partAId] }),
  });
};

// ---------- Employee-facing ----------
export const useMyForm16List = (employeeNo) =>
  useQuery({
    queryKey: ['my-form16-list', employeeNo],
    queryFn: () =>
      axiosInstance.get(`api/form16/employee/${employeeNo}/`).then((r) => r.data),
    enabled: !!employeeNo,
  });

export const useMyForm16Detail = (employeeNo, id) =>
  useQuery({
    queryKey: ['my-form16-detail', employeeNo, id],
    queryFn: () =>
      axiosInstance
        .get(`api/form16/employee/${employeeNo}/${id}/`)
        .then((r) => r.data),
    enabled: !!employeeNo && !!id,
  });

// ---------- Rectifications ----------
export const useRectifications = (params = {}) =>
  useQuery({
    queryKey: ['rectifications', params],
    queryFn: () =>
      axiosInstance.get('api/form16/rectifications/', { params }).then((r) => r.data),
  });

export const useCreateRectification = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v !== undefined && v !== null) fd.append(k, v);
      });
      return axiosInstance
        .post('api/form16/rectifications/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rectifications'] }),
  });
};

export const useUpdateRectification = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      axiosInstance
        .patch(`api/form16/rectifications/${id}/`, payload)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rectifications'] }),
  });
};

// Annual summary Excel export — one row per employee for a FY (companion to
// the PDF certificates). Mirrors the blob-download pattern used by
// downloadPayrollReport in api/index.js: fetch as a blob, then trigger the
// browser download via a manufactured <a download>.
export const downloadForm16ExcelSummary = async ({ fyId, fyLabel, employeeNo, branch } = {}) => {
  const params = { fy_id: fyId };
  if (employeeNo) params.employee_no = employeeNo;
  if (branch) params.branch = branch;
  const response = await axiosInstance.get('api/form16/export-excel/', { params, responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  const tag = (fyLabel || fyId || 'FY').toString().replace(/[^\w-]+/g, '-');
  link.setAttribute('download', `Form16_Summary_${tag}.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ---------- PDF download URL helpers ----------
export const form16PdfUrl = (id, part = 'combined') => {
  const base = `${tenantBaseURL()}api/form16/${id}/pdf/${part}/`;
  try {
    const data = JSON.parse(localStorage.getItem('authData') || 'null');
    const token = data?.admin?.Token || data?.token;
    if (token) {
      return `${base}?token=${encodeURIComponent(token)}`;
    }
  } catch (e) {
    // fallback to base
  }
  return base;
};
